import { useEffect, useRef, type ReactNode } from 'react';

// A modal for the club social pages. HAND-ROLLED and rendered IN the tree, not a
// Radix portal: a portaled dialog lands on <body>, outside `.kyck-portal`, and takes
// the staff theme tokens, so it would be light inside a dark portal (the reason the
// portal-assets detail is a page, not a dialog). In the tree it inherits the mode.
//
// Escape closes it, focus moves into it on open and returns to whatever had it, and
// the page behind is inert to the pointer. `dismissOnOverlay` is OFF by default: a
// click outside must never throw away work in progress (the AI caption workspace).

export function PortalDialog({
  title,
  onClose,
  children,
  wide = false,
  dismissOnOverlay = false,
  testId,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  dismissOnOverlay?: boolean;
  testId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      before?.focus?.();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3"
      onMouseDown={(e) => {
        if (dismissOnOverlay && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-testid={testId}
        className={`flex max-h-[92vh] w-full flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-xl outline-none ${wide ? 'max-w-6xl' : 'max-w-2xl'}`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="rounded px-2 py-1 text-sm text-muted-foreground hover:bg-accent" aria-label="Close">
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
